import React, { Component } from 'react';
import Autosuggest from 'react-autosuggest';
import { Col, Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import CompanyContext from '../../auth/CompanyContext';
import ConfirmButton from '../Modal/ConfirmButton';
import '../Autocomplete/Autocomplete.css';

const loadingImg = 'https://cdn.auth0.com/blog/auth0-react-sample/assets/loading.svg';

class CompanyCustodians extends Component {
    static contextType = CompanyContext;

    state = {
        custodians: [],
        options: [],
        suggestions: [],
        value: '',
        loading: true
    };

    componentDidMount() {
        this.loadCustodians();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.secID !== this.props.secID || prevProps.clientToken !== this.props.clientToken) {
            this.loadCustodians();
        }
    }

    headers = (includeContentType = false) => ({
        ...(includeContentType ? { 'Content-Type': 'application/json' } : {}),
        'Authorization': 'Bearer ' + this.props.clientToken,
        'X-Database': this.context.company
    });

    loadCustodians = () => {
        if (!this.props.secID || !this.props.clientToken) return;

        this.setState({ loading: true });
        fetch('/api/companies/custodians/' + encodeURIComponent(this.props.secID), {
            method: 'GET',
            headers: this.headers()
        })
            .then(results => results.json())
            .then(results => {
                const custodians = results.data || [];
                this.setState({
                    custodians,
                    options: results.options || [],
                    suggestions: [],
                    value: '',
                    loading: false
                });
                if (this.props.onChange) this.props.onChange(custodians);
            })
            .catch(() => this.setState({ loading: false }));
    };

    availableOptions = () => {
        const selectedIDs = new Set(this.state.custodians.map(custodian => custodian.id));
        return this.state.options.filter(option => !selectedIDs.has(option.id));
    };

    onSuggestionsFetchRequested = ({ value }) => {
        const query = String(value || '').trim().toLowerCase();
        const suggestions = this.availableOptions()
            .filter(option => !query || String(option.name || '').toLowerCase().includes(query))
            .slice(0, 20);
        this.setState({ suggestions });
    };

    onSuggestionsClearRequested = () => {
        this.setState({ suggestions: [] });
    };

    onSuggestionSelected = (event, { suggestion }) => {
        this.addCustodian(suggestion);
    };

    addCustodian = (selected) => {
        if (!selected || this.state.loading) return;

        this.setState({ value: selected.name, loading: true });
        fetch('/api/companies/custodians/' + encodeURIComponent(this.props.secID), {
            method: 'POST',
            body: JSON.stringify({ custodianId: selected.id, auth0: this.props.user }),
            headers: this.headers(true)
        })
            .then(results => results.json())
            .then(results => {
                if (results.success) this.loadCustodians();
                else this.setState({ loading: false });
            })
            .catch(() => this.setState({ loading: false }));
    };

    deleteCustodian = (custodianId) => {
        this.setState({ loading: true });
        fetch('/api/companies/custodians/' + encodeURIComponent(this.props.secID) + '/' + custodianId, {
            method: 'DELETE',
            headers: this.headers()
        })
            .then(results => results.json())
            .then(results => {
                if (results.success) this.loadCustodians();
                else this.setState({ loading: false });
            })
            .catch(() => this.setState({ loading: false }));
    };

    onInputChange = (event, { newValue }) => {
        const value = typeof newValue === 'string' ? newValue : '';
        this.setState({ value });
    };

    renderSuggestion = (suggestion) => (
        <div className="suggestionContainer">
            <div className="suggestionName">{suggestion.name}</div>
        </div>
    );

    render() {
        const canConfigureCustodians = typeof this.props.checkPermission === 'function'
            && this.props.checkPermission('Custodians');
        const inputProps = {
            placeholder: 'Custodian Search',
            value: this.state.value,
            onChange: this.onInputChange,
            autoComplete: 'off'
        };

        return (
            <>
                {this.state.loading && (
                    <div className="searchContainer mb-3" style={{ minHeight: '38px' }}>
                        <div className="searchLoader"><img src={loadingImg} alt="Loading..." /></div>
                    </div>
                )}
                {!this.state.loading && this.state.options.length > 0 && (
                    <div className="searchContainer mb-3">
                        <Autosuggest
                            clientToken={this.props.clientToken}
                            suggestions={this.state.suggestions}
                            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                            onSuggestionSelected={this.onSuggestionSelected}
                            getSuggestionValue={suggestion => suggestion.name}
                            renderSuggestion={this.renderSuggestion}
                            inputProps={inputProps}
                            className="autosuggest"
                        />
                    </div>
                )}
                {this.state.custodians.map(custodian => (
                    <Row key={custodian.id} className="align-items-center mb-2">
                        <Col>{custodian.name}</Col>
                        <Col xs="auto">
                            <ConfirmButton
                                color="danger"
                                title="Remove Custodian"
                                disabled={this.state.loading}
                                onClick={() => this.deleteCustodian(custodian.id)}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                            </ConfirmButton>
                        </Col>
                    </Row>
                ))}
                {!this.state.loading && this.state.options.length === 0 && (
                    <Row>
                        <Col className="text-muted">
                            {canConfigureCustodians
                                ? <Link to="/system-configuration">Configure Custodians</Link>
                                : 'No custodians available.'}
                        </Col>
                    </Row>
                )}
                {!this.state.loading && this.state.options.length > 0 && this.state.custodians.length === 0 && (
                    <Row><Col className="text-muted">No custodians have been assigned to this company.</Col></Row>
                )}
            </>
        );
    }
}

export default CompanyCustodians;
